/** Align with signup (`openpay.js`) + Weglot; `users.language` drives email templates. */
export const SUPPORTED_USER_LANGUAGES = ['en', 'pl', 'fr', 'it', 'de', 'pt', 'pt-br', 'ko', 'es', 'ar'];

/**
 * Normalize a language code against SUPPORTED_USER_LANGUAGES.
 * Keeps region tags when the full code is supported (e.g. `pt-br`); otherwise falls back to the base (`fr-CA` → `fr`).
 * Note: `pt-br` and `br` are different — we never treat `br` as Brazilian Portuguese.
 */
export function normalizeLanguageCode(raw) {
    if (raw === undefined || raw === null) return null;
    let code = String(raw).trim().toLowerCase().replace(/_/g, '-').slice(0, 10);
    if (!code) return null;
    if (SUPPORTED_USER_LANGUAGES.includes(code)) return code;
    if (code.includes('-')) {
        const base = code.split('-')[0];
        if (SUPPORTED_USER_LANGUAGES.includes(base)) return base;
    }
    return null;
}

/** Resolve to a supported language, defaulting to `fallback` (usually `en`). */
export function resolveSupportedUserLanguage(raw, fallback = 'en') {
    return normalizeLanguageCode(raw) || fallback;
}

/** Language code for Weglot.switchTo() — same as Plixi (`pt-br` stays `pt-br`). */
export function toWeglotLanguageCode(raw) {
    return normalizeLanguageCode(raw) || 'en';
}

/** Map Weglot codes back to Plixi language codes. */
export function fromWeglotLanguageCode(raw) {
    return normalizeLanguageCode(raw) || 'en';
}

/**
 * Pick a translation dictionary key: exact match → base (`pt-br` → `pt`) → `en`.
 * Prefer this over duplicating every `pt` string as `"pt-br"`.
 */
export function pickTranslationLang(lang, dict) {
    if (!dict || typeof dict !== 'object') return 'en';
    const code = normalizeLanguageCode(lang) || String(lang || 'en').toLowerCase();
    if (dict[code]) return code;
    const base = code.includes('-') ? code.split('-')[0] : code;
    if (base && dict[base]) return base;
    if (dict.en) return 'en';
    return Object.keys(dict)[0] || 'en';
}

/** Value lookup helper: `dict[pickTranslationLang(lang, dict)]`. */
export function pickTranslation(lang, dict) {
    if (!dict) return undefined;
    return dict[pickTranslationLang(lang, dict)];
}

/**
 * For Weglot/bootstrap + URL `lang`: non-empty only when supported and not English.
 * Used from Marko `$` lines (avoid nested `$ if` / chained splits that break the compiler).
 */
export function savedLangSnippetFromUser(raw) {
    const code = normalizeLanguageCode(raw);
    if (!code || code === 'en') return '';
    return code;
}

export const USER_LANGUAGE_LABELS = {
    en: 'English',
    pl: 'Polski',
    fr: 'Français',
    it: 'Italiano',
    de: 'Deutsch',
    pt: 'Português',
    'pt-br': 'Português (Brasil)',
    ko: '한국어',
    es: 'Español',
    ar: 'العربية'
};

/** Active language from browser URL path prefix and/or `?lang=` (query wins). */
export function resolveLanguageCodeFromUrl(pathname, search) {
    let code = null;
    const segments = String(pathname || '').split('/').filter(Boolean);
    const seg0 = segments.length ? segments[0].toLowerCase().slice(0, 10) : '';
    if (seg0 && SUPPORTED_USER_LANGUAGES.includes(seg0)) {
        code = seg0;
    }
    const ql = normalizeLanguageCode(
        search ? new URLSearchParams(search).get('lang') : null,
    );
    if (ql) code = ql;
    return code || 'en';
}

/** After `languageUrlPrefix` middleware: path lang on `req.plixiUrlLang`, query on `req.query.lang`. */
export function resolveLanguageCodeFromRequest(req) {
    const fromQuery = normalizeLanguageCode(req?.query?.lang);
    if (fromQuery) return fromQuery;
    const fromPath = normalizeLanguageCode(req?.plixiUrlLang);
    if (fromPath) return fromPath;
    return 'en';
}

/** Prefix a path with `/{lang}` when not English; strips any existing lang prefix first. */
export function prefixPathWithLanguage(path, langCode) {
    const code = normalizeLanguageCode(langCode) || 'en';
    const raw = String(path || '/');
    const qIndex = raw.indexOf('?');
    const pathname = qIndex >= 0 ? raw.slice(0, qIndex) : raw;
    const query = qIndex >= 0 ? raw.slice(qIndex) : '';
    const segments = pathname.split('/').filter(Boolean);
    const first = segments.length ? segments[0].toLowerCase() : '';
    const hasLang = first && SUPPORTED_USER_LANGUAGES.includes(first);
    const rest = hasLang ? segments.slice(1) : segments;
    if (code === 'en') {
        return (rest.length ? `/${rest.join('/')}` : '/') + query;
    }
    return `/${[code, ...rest].join('/')}` + query;
}
